/*
 * © Copyright 2014 - 2022 Micro Focus or one of its affiliates.
 *
 * The only warranties for products and services of Micro Focus and its affiliates and licensors ("Micro Focus") are as
 * may be set forth in the express warranty statements accompanying such products and services. Nothing herein should be
 * constituting an additional warranty. Micro Focus shall not be liable for technical or editorial errors or omissions
 * contained herein.
 *
 * The information contained herein is subject to change without notice.
 */

// eslint-disable-next-line
import * as tracking from 'exports-loader?tracking!tracking';
import 'tracking/build/data/face.js';
import t from '../../i18n/locale-keys';

// Error code 1006 means that the connection was closed abnormally
// See http://tools.ietf.org/html/rfc6455#section-7.4.1
const WS_ABNORMAL_CLOSURE_CODE = 1006;
// Error code 1000 means that the connection was closed normally.
const WS_NORMAL_CLOSURE_CODE = 1000;
const CAPTURE_TIMEOUT = 60000;  // 60 seconds
const FACE_SERVICE_URL = 'wss://127.0.0.1:8441/api/v1/video';
export const captureButtonID = "start_capture";
export const captureInstClass = "face-inst";
const FACE_API_PROVIDERS = {
    AZURE_COGNITIVE: 'azure_cognitive_provider',
    CONTACTABLE_KYC: 'contactable_kyc_provider'
};

let webSocketInterval = null;
let trackerTaskInterval = null;
let trackertask = null;
let stop = false;
let webSocket = null;
let startCapture = false;
let ovalPattern = false;
let tracker = null;
let timeoutTimerID = null;


//========================= starts Device Service =============================
export function deviceServiceFaceCapture(callback, switchCallback, imageParams, timeouts) {
    const imageData = imageParams || {};
    if ('WebSocket' in window) {
        let counter = 0;
        const WAIT_COUNTER = 5;  // no of successful face detection before autosubmit
        console.log("ImageParams",imageData);
        const params = {
            'params': {
                'detectFace': 'true',
                ...imageData              // height, width, and image format
            },
            'command': 'getImage'
        };
        if (!webSocket) {
            clearInterval(webSocketInterval); // Cleanup existing Interval If any.
            webSocket = new WebSocket(FACE_SERVICE_URL);
        } else if (webSocket.readyState === WebSocket.CLOSING) {
            callback({ 'result': 'ERROR', 'msg': t.webcamBusy() });
            webSocketInterval = setInterval(function () {
                checkWebSocketAlive(callback, switchCallback, imageParams, timeouts);
            }, 1000);
        }

        webSocket.onopen = function() {
            webSocket.send(JSON.stringify(params));
            showInstructions('device');
        };
        webSocket.onmessage = function(evt) {
            if (!stop) {
                const msg = JSON.parse(evt.data);
                if (msg.result === 'OK') {
                    // eslint-disable-next-line
                    msg.imgSrc = 'data:image/png;base64,' + encodeURIComponent(msg.data);
                    if (msg.detectedObjects) {
                        if (msg.detectedObjects.face) {
                            callback({
                                result: counter === WAIT_COUNTER ? 'OK' : 'WAIT',
                                faceImg: msg.data,
                                imgSrc: msg.imgSrc
                            });
                            counter++;
                        }
                    }
                    if (webSocket.readyState === WebSocket.OPEN && !stop) {
                        webSocket.send(JSON.stringify(params));
                    }
                }
                else {
                    callback({'result': 'ERROR', 'msg': msg.reason});
                }
            } else {
                if (webSocket) {
                    webSocket.close();
                }
            }
        };
        webSocket.onclose = function(evt) {
            if (evt.code !== WS_NORMAL_CLOSURE_CODE) {
                console.log('Websocket Error: {code: %s, reason: %s}', evt.code, evt.reason || 'Unknown');
            }
            // close event returning with error code 1006 for websocket connection error
            // if error code is 1006 and stop flag is not set, switch to browser support
            if (evt.code === WS_ABNORMAL_CLOSURE_CODE && !stop) {
                clearInterval(webSocketInterval);
                showInstructions();
                switchCallback(callback, imageData, timeouts);
            }

            if (webSocket.readyState === WebSocket.CLOSED) {
                webSocket = null;
            }
        };
    }

    else {
        showInstructions();
        switchCallback(callback, imageData, timeouts);
    }
}

export function checkWebSocketAlive(callback, ...args) {
    if (webSocket === null) {
        clearInterval(webSocketInterval);
        deviceServiceFaceCapture(callback, ...args);
    }
}

//=================== ends Device Service ===========================


//=================== starts Browser Support (trackingJS) ===========================

/**
* Captures the user camera when tracking a video element and set its source
* to the camera stream.
* @param {HTMLVideoElement} element Canvas element to track.
* @param {object} optOptions Optional configuration to the tracker.
*/
// eslint-disable-next-line
tracking.initUserMedia_ = function(element, optOptions, failCallback) {
    const navigator = window.navigator;
    // Older browsers might not implement mediaDevices at all, so we set an empty object first
    if (navigator.mediaDevices === undefined) {
      navigator.mediaDevices = {};
    }
    // Some browsers partially implement mediaDevices. We can't just assign an object
    // with getUserMedia as it would overwrite existing properties.
    // Here, we will just add the getUserMedia property if it's missing.
    if (navigator.mediaDevices.getUserMedia === undefined) {
      navigator.mediaDevices.getUserMedia = function(constraints) {
        // First get ahold of the legacy getUserMedia, if present
        const getUserMedia = navigator.webkitGetUserMedia || navigator.mozGetUserMedia;

        // Some browsers just don't implement it - return a rejected promise with an error
        // to keep a consistent interface
        if (!getUserMedia) {
          return Promise.reject(new Error('getUserMedia is not implemented in this browser'));
        }

        // Otherwise, wrap the call to the old navigator.getUserMedia with a Promise
        return new Promise(function(resolve, reject) {
          getUserMedia.call(navigator, constraints, resolve, reject);
        });
      };
    }

    navigator.mediaDevices.getUserMedia({
        video: optOptions.video || true,
        audio: !!(optOptions && optOptions.audio)
    })
    .then(function(stream) {
        window.tracking.localStream = stream;
        // Older browsers may not have srcObject
        if ('srcObject' in element) {
            element.srcObject = stream;
        } else {
            // Avoid using this in new browsers, as it is going away.
            element.src = window.URL.createObjectURL(stream);
        }
    })
    .catch(function() {
        failCallback(t.webcamNotSupported());
    });
};


// overriding track function, to add failCallback as parameter that catches the error from getUserMedia
tracking.track = function(element, tracker, optOptions, failCallback) {
    // eslint-disable-next-line
    element = tracking.one(element);

    if (optOptions) {
        if (optOptions.camera) {
            // eslint-disable-next-line
            tracking.initUserMedia_(element, optOptions, failCallback);
        }
    }

    // eslint-disable-next-line
    return tracking.trackVideo_(element, tracker, optOptions);
};

export function browserFaceCapture(domIds, callback, imageParams, timeouts, faceCallBacks) {
    const canvas = document.getElementById(domIds.canvas);
    const context = canvas.getContext('2d');
    const WAIT_COUNTER = 10;  // no of successful face detection before autosubmit
    const btn = document.getElementById(captureButtonID);
    const xco = canvas.width / 2;
    const yco = (canvas.height-20) / 2;
    let counter = 0;

    // to catch error throw by getUserMedia
    function failCallback(err) {
        callback({ 'result': 'ERROR', 'msg': err });
    }

    if (!startCapture) {
        ovalPattern = false;
        timeouts.endTimeout();//clean up the timeout started for device service initally

        tracker = new tracking.ObjectTracker('face');
        tracker.setInitialScale(6);
        tracker.setStepSize(2);
        tracker.setEdgesDensity(0.1);
        const opt_options = {
            camera: true,
            video: imageParams && imageParams.height ? { height: { ideal: imageParams.height } } : true
        };
        trackertask = tracking.track('#' + domIds.video, tracker, opt_options, failCallback);
        context.fillStyle = "rgba(0, 0, 0, 0.7)";
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.strokeStyle = "white";

        btn.addEventListener("click", () => {
            timeouts.startTimeout(); // start the timer for capturing images from browser API's,after capture btn click
            startCapture = true;
            btn.disabled = true;
            //Show the capturing progress bar or anyother UI activity ,if require
            if(faceCallBacks && faceCallBacks.startCapture){
                faceCallBacks.startCapture();
            }
        });
    }

    if (!trackerTaskInterval) {
        trackerTaskInterval = setInterval(checkTrackerTaskAlive, 1000);
    }
    
    //check if there are already some tracker track func running,then clean it up,before
    //assigning a new one.By default the first tracker track should be kept from intial setup
    if (startCapture) {
        if (tracker && tracker.events_ && tracker.events_.track) {
            if (tracker.events_.track.length > 1) {
                tracker.events_.track.pop();
            }
        }
    }
    tracker.on('track', function (event) {
        if (!ovalPattern) {
            //default oval width&height
            let width = 120, height = 90;
            if (imageParams.format) {
                //update the width &height for facialrec method to match its video dimesnions
                width = 150;
                height = 110;
            }
            fillCircle(context, xco, yco, width, height);
            clearCircle(context, xco, yco, width, height);
            ovalPattern = true;
            btn.classList.remove('test-btn-hide');
        }
        event.data.forEach(function (rect) {
            if (startCapture) {
                if (counter === WAIT_COUNTER) {
                    let faceImg = takeSnapshot(domIds);
                    if (faceImg) {
                        faceImg = faceImg.split('base64,')[1];
                    }
                    callback({ 'result': 'OK', faceImg });

                }
                counter++;
            }
        });
    });

}

//This functions shows differnt Instructions block(device/browser) for facial video
function displayInst(element, display) {
    const el = document.querySelector(element);
    if (el)
        el.style.display = display;
}
function showInstructions(type){
    const deviceService = `.${captureInstClass} ul:first-child`;
    const browserAPI = `.${captureInstClass} ul:last-child`;
    if (type === 'device') {
        displayInst(deviceService, 'block');
        displayInst(browserAPI, 'none');

    } else {
        displayInst(deviceService, 'none');
        displayInst(browserAPI, 'block');
    }
}

export function finishCapturing() {
    //reset the capturing flags
    startCapture = false;
    ovalPattern = false;
    //Clear the Timeouts ,if any after capturing is done or cancel/close.
    if (timeoutTimerID) {
        clearTimeout(timeoutTimerID);
        timeoutTimerID = null;
    }
}

export function takeSnapshot(domIds) {
    const video = document.getElementById(domIds.video);
    const canvas = document.getElementById(domIds.canvasSnap);
    const context = canvas.getContext('2d');
    // Get the exact size of the video element.
    const width = video.videoWidth;
    const height = video.videoHeight;

    // Set the canvas to the same dimensions as the video.
    canvas.width = width;
    canvas.height = height;

    // Draw a copy of the current frame from the video on the canvas.
    context.clearRect(0, 0, width, height);
    context.drawImage(video, 0, 0, width, height);
    return canvas.toDataURL('image/png');
}

export function checkTrackerTaskAlive() {
    if (!trackertask.inRunning()) {
        trackertask.run();
    } else {
        stoptrackerTaskInterval();
    }
}

export function stoptrackerTaskInterval() {
    if (trackerTaskInterval) {
        clearInterval(trackerTaskInterval);
    }
}

const fillCircle = function (context,x, y, radiusX, radiusY) {
    context.beginPath();
    context.strokeStyle = "white";
    context.lineWidth = "8";
    context.ellipse(x, y, radiusX, radiusY, Math.PI / 2, 0, 2 * Math.PI);
    context.stroke();

};

const clearCircle = function (context,x, y, radiusX, radiusY) {
    context.beginPath();
    context.ellipse(x, y, radiusX, radiusY, Math.PI / 2, 0, 2 * Math.PI);
    context.clip();
    context.clearRect(x - radiusX - 1, y - radiusX - 1,
        radiusX * 2 + 2, radiusX * 2 + 2);
};

//====================== ends Browser support (trackingJS)=============================================

 /**
     * It captures the face of user.
     * It uses the Device Service if available else it switches to Browser Support
     * (trackingJS) to capture the face of user
    **/
export function captureFace(facialVideoKey, changeStateCallBack, faceApiProvider, imageParams, faceCallBacks) {
    const domIds = {
        canvas: 'canvas' + facialVideoKey,
        video: 'video' + facialVideoKey,
        canvasSnap: 'canvasSnap' + facialVideoKey
    };

    const getProviderImageParams = () => {
        switch (faceApiProvider) {
            case FACE_API_PROVIDERS.AZURE_COGNITIVE:
                return { height: 400, width: 400, format: "PNG" };
            case FACE_API_PROVIDERS.CONTACTABLE_KYC:
                return { height: 1024, width: 1024, format: "JPEG" };
            default:
                return imageParams || {};     // Provider undefined
        }
    };

    const imageData = getProviderImageParams();

    stop = false;
    // Make fetch request
    let promisePending = true;
    const clearTimeoutTimer = () => {
        if (timeoutTimerID) {
            clearTimeout(timeoutTimerID);
        }
    };

    return new Promise((resolve, reject) => {
        function captureFaceCallBack(data) {
            if (!promisePending) {
                return;
            }

            if (data.result === 'OK') {
                changeStateCallBack({imgSrc: ''});
                clearTimeoutTimer();
                promisePending = false;
                resolve(data.faceImg);
            }
            else if (data.result === 'WAIT') {
                changeStateCallBack({imgSrc: data.imgSrc});
            }
            else {
                clearTimeoutTimer();
                promisePending = false;
                reject(data.msg);
            }
        }

        function setFaceTimeout() {
            clearTimeoutTimer(); //Clear any existing timeout If any,In case of multiple calls for captureFace fn
            timeoutTimerID = setTimeout(() => {
                if (promisePending) {
                    promisePending = false;
                    reject({ status: 'timeout' });
                }
            }, CAPTURE_TIMEOUT);
        }
        const timeouts = {
            startTimeout: setFaceTimeout,
            endTimeout: clearTimeoutTimer
        }

        deviceServiceFaceCapture(captureFaceCallBack, (callback, imageData, timeouts) => browserFaceCapture(domIds, callback, imageData, timeouts, faceCallBacks), imageData, timeouts);
        setFaceTimeout();    

    });
}

export function stopCapture() {
    finishCapturing();
    if (!stop) {
        stop = true;
        if (window.tracking.localStream) {
            const track = window.tracking.localStream.getTracks()[0];
            track.stop();
        }
        if (trackertask) {
            setTimeout(function() {
                trackertask.stop();
            }, 10);
        }
    }
}
